import { useEffect, useState, useRef, useCallback } from 'react';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const stats = [
  { value: 34672, label: 'Pescadores e Pescadoras Artesanais representados em nossa base', suffix: '' },
  { value: 2321, label: 'Pescadores e Pescadoras aposentados pelo Sindpesca', suffix: '' },
  { value: 132, label: 'Colaboradores em nosso time nos 29 Municípios de atuação', suffix: '' },
];

function formatNumber(num) {
  return num.toLocaleString('pt-BR');
}

function useCountUp(target, duration = 2000, start = false) {
  const [count, setCount] = useState(0);
  const frameRef = useRef(null);

  const animate = useCallback(() => {
    const startTime = performance.now();
    const step = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) {
        frameRef.current = requestAnimationFrame(step);
      }
    };
    frameRef.current = requestAnimationFrame(step);
  }, [target, duration]);

  useEffect(() => {
    if (start) animate();
    return () => { if (frameRef.current) cancelAnimationFrame(frameRef.current); };
  }, [start, animate]);

  return count;
}

function StatItem({ stat, started, delay }) {
  const count = useCountUp(stat.value, 2500, started);
  const [ref, vis] = useScrollReveal(0.3);

  return (
    <div
      ref={ref}
      data-testid={`stat-${stat.value}`}
      className={`text-center transition-all duration-700 ${vis ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <p className="counter-number text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#1A8C7A] mb-3">
        {formatNumber(count)}
      </p>
      <p className="text-sm sm:text-base text-[#4A5568] max-w-xs mx-auto leading-relaxed">
        {stat.label}
      </p>
    </div>
  );
}

export default function Statistics() {
  const [started, setStarted] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStarted(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} data-testid="statistics-section" className="py-20 sm:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <span className="inline-block px-4 py-1.5 bg-[#1A8C7A]/10 text-[#1A8C7A] text-xs font-bold tracking-wider uppercase rounded-full mb-4">
            Nossos Números
          </span>
          <h2
            data-testid="statistics-title"
            className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#1A1A2E] mb-4"
            style={{ letterSpacing: '-0.02em' }}
          >
            GARANTINDO O SUSTENTO DE QUEM
            <br className="hidden sm:block" />
            <span className="text-[#1A8C7A]"> VIVE DAS ÁGUAS</span>
          </h2>
          <div className="w-20 h-1 bg-[#E8621A] mx-auto rounded-full" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8">
          {stats.map((stat, i) => (
            <StatItem key={i} stat={stat} started={started} delay={i * 200} />
          ))}
        </div>
      </div>
    </section>
  );
}
